'use client';

import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigation } from './navigation/navigation-context';
import { useOrderListSheet } from '@/app/[locale]/order-list/order-list-sheet-context';
import { useBottomPosition } from '@/hooks/use-bottom-position';
import { DateFormat, formatUnixTime } from '@/lib/datetime';

interface OrderNotification {
  order_code: string;
  store_name: string;
  order_status: string;
  total_payment: number;
  time: number;
}

interface OrderNotificationToastProps {
  notification: OrderNotification | null;
  onClose: () => void;
}

export function OrderNotificationToast({
  notification,
  onClose,
}: OrderNotificationToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const { openSheet } = useOrderListSheet();
  const { bottomPosition } = useBottomPosition();
  const { isNavigationVisible } = useNavigation();

  // Detect Android to handle platform-specific touch event issues
  const isAndroid =
    typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

  const formatTime = (timestamp: number) => {
    return formatUnixTime(timestamp, DateFormat.DATE_TIME_24);
  };

  useEffect(() => {
    if (notification) {
      // Don't show toast if tracking sheet is open
      if (document.documentElement.dataset.trackingSheetOpen === 'true') {
        console.log(
          '[OrderNotificationToast] Tracking sheet open, skipping toast'
        );
        return;
      }
      setIsVisible(true);
    }
  }, [notification]);

  // Auto-close toast when tab becomes hidden to prevent overlay stacking
  useEffect(() => {
    if (!isVisible) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[OrderNotificationToast] Tab hidden, closing toast');
        setIsVisible(false);
        setTimeout(() => {
          onClose();
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isVisible, onClose]);

  const getStatusInfo = (status: string) => {
    const statusMap = {
      CONFIRMING: { text: 'Đang chờ xác nhận' },
      CONFIRMED: { text: 'Đã xác nhận' },
      ASSIGNING: { text: 'Đang tìm tài xế' },
      ACCEPTED: { text: 'Tài xế đã nhận' },
      'IN PROCESS': { text: 'Đang giao đến bạn' },
      BOARDED: { text: 'Đang giao đến bạn' },
      COMPLETED: { text: 'Đã hoàn thành' },
      IN_RETURN: { text: 'Đang hoàn hàng' },
      RETURNED: { text: 'Đã hoàn hàng' },
      CANCELLED: { text: 'Đã hủy' },
      BOT_CANCELLED: { text: 'Hệ thống hủy' },
      FAILED: { text: 'Thất bại' },
      IDLE: { text: 'Đang tìm tài xế' },
    };
    return statusMap[status as keyof typeof statusMap] || { text: status };
  };

  const handleNotificationClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isOpening) return; // Prevent double-tap

    setIsOpening(true);
    setIsVisible(false);

    if (isAndroid) {
      // Android: Close toast immediately to avoid overlay blocking issues
      onClose();
      requestAnimationFrame(() => {
        if (notification) {
          openSheet(notification.order_code);
        }
        setTimeout(() => setIsOpening(false), 500);
      });
    } else {
      // iOS/Desktop: Wait for toast animation, then open sheet
      setTimeout(() => {
        if (notification) {
          openSheet(notification.order_code);
        }
        onClose();
        setIsOpening(false);
      }, 300); // Match toast fade-out duration
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  if (!notification) return null;

  return (
    <Card
      className={`
        fixed inset-x-4 w-vw z-[9985] bg-white 
        rounded-lg shadow-lg transition-all
        duration-300 ease-in-out
        ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }
        ${isOpening ? 'pointer-events-none' : ''}
      `}
      onClick={handleNotificationClick}
      style={{ bottom: isNavigationVisible ? bottomPosition : 16 }}
    >
      <div className="p-4 w-full">
        {/* Header với close button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div>
              <p className="text-sm text-neutral-50">
                Đơn #{notification.order_code} {'  '}{' '}
                {formatTime(notification.time)}
              </p>
              <div className="flex items-center space-x-1 w-full">
                <p className="font-semibold text-neutral-90 line-clamp-1 w-auto">
                  {notification.store_name
                    ? notification.store_name
                    : 'Cửa hàng'}{' '}
                  —
                </p>
                <span className="text-primary-50 ml-1 font-semibold flex-none">
                  {getStatusInfo(notification.order_status).text}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleClose}
            size="icon"
            className="rounded-full transition-colors [&_svg]:size-7 text-neutral-40 hover:bg-neutral-10 flex-none -mr-3"
          >
            <X />
          </Button>
        </div>

        {/* Status indicator */}
        {/* <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div className="size-2 bg-primary-50 rounded-full animate-pulse" />
            <div className="size-2 bg-primary-30 rounded-full" />
            <div className="size-2 bg-neutral-20 rounded-full" />
          </div>
          <span className="text-sm text-neutral-60">
            Trạng thái: {notification.order_status}
          </span>
        </div>

        <div className="mt-2">
          <p className="text-lg font-bold text-neutral-90">
            {formatCurrency(notification.total_payment)}
          </p>
        </div> */}
      </div>
    </Card>
  );
}
